import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Pencil, CheckCircle2, X } from "lucide-react";
import { useAuth } from "../lib/auth";

/**
 * Read Me — single-popup replacement for the old sticky-note wall.
 * Clean, minimal popup that matches the app's white/light + #0066ff aesthetic.
 * Closing (either via ✕ or "I've read this") returns to the previous route.
 */
export default function ReadMe() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canEdit = user?.role === "admin" || user?.role === "readme_author";
  const [msg, setMsg] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/readme");
      setMsg(data);
      setDraft(data.text || "");
    } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  const confirmRead = async () => {
    try {
      await api.post("/readme/read");
      toast.success("Marked as read");
      goBack();
    } catch { toast.error("Failed"); }
  };

  const save = async () => {
    if (!draft.trim()) { toast.error("Message can't be empty"); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/readme", { text: draft });
      setMsg(data); setEditing(false); toast.success("Saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div
      data-testid="readme-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget && !editing) goBack(); }}
      className="fixed inset-0 z-40 flex items-center justify-center bg-zinc-900/25 p-4 backdrop-blur-sm"
    >
      <div
        data-testid="readme-modal"
        className="relative w-full max-w-lg rounded-2xl border border-zinc-200 bg-white p-8 shadow-2xl"
      >
        <button
          data-testid="readme-close"
          onClick={goBack}
          className="absolute right-4 top-4 rounded-full p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
          title="Close"
        >
          <X size={18} />
        </button>

        <div className="text-xs font-semibold uppercase tracking-widest text-[#0066ff]">Read Me</div>

        {!msg ? (
          <div className="mt-6 text-sm text-zinc-500">Loading…</div>
        ) : editing ? (
          <>
            <textarea
              data-testid="readme-editor"
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={5}
              className="mt-5 w-full rounded-xl border border-zinc-200 bg-white p-4 text-base leading-relaxed text-zinc-800 focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20"
            />
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                onClick={() => { setDraft(msg.text || ""); setEditing(false); }}
                className="rounded-md px-3 py-2 text-sm font-medium text-zinc-600 hover:bg-zinc-100"
              >Cancel</button>
              <button
                data-testid="readme-save"
                onClick={save}
                disabled={saving}
                className="rounded-md bg-[#0066ff] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0052cc] disabled:opacity-50"
              >{saving ? "Saving…" : "Save message"}</button>
            </div>
          </>
        ) : (
          <>
            <p
              data-testid="readme-text"
              className="mt-6 whitespace-pre-wrap text-center text-2xl font-medium leading-relaxed text-zinc-900"
              style={{ fontFamily: '"Kalam", "Caveat", cursive' }}
            >
              {msg.text}
            </p>
            <div className="mt-4 text-center text-xs text-zinc-500">
              Last updated by <span className="font-medium text-zinc-700">{msg.updated_by || "—"}</span>
              {msg.has_read ? <> · <span className="text-emerald-600">You've read this</span></> : null}
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
              {canEdit && (
                <button
                  data-testid="readme-edit"
                  onClick={() => setEditing(true)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
                >
                  <Pencil size={14} /> Edit
                </button>
              )}
              <button
                data-testid="readme-confirm"
                onClick={confirmRead}
                className="inline-flex items-center gap-2 rounded-md bg-[#0066ff] px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#0052cc] active:scale-[0.98]"
              >
                <CheckCircle2 size={16} /> I've read this — take me back
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
